import { forwardRef, ReactNode, useState } from 'react'
// eslint-disable-next-line no-restricted-imports
import { Breakpoint, Container } from '@mui/material'
import { useRouter } from 'next/router'
import { makeStyles } from 'tss-react/mui'
import BaseSwitchBanner from '~/src/shared/components/feedback/BaseSwitchBanner'
import MultiVenueBanner from '~/src/shared/components/feedback/MultiVenueBanner'
import { useLayout } from '~/src/shared/components/templates/LayoutContext'
import { Domain } from '~/src/shared/types'

interface IMainContentContainerProps {
  domain: Domain
  maxWidth?: Extract<Breakpoint, 'sm' | 'md' | 'lg'>
  centered?: boolean
  gap?: string
  /**
   * Extra pixels to subtract from the scroll region's height, on top of the app bar. Use this to
   * reserve room for a full-bleed element rendered as a sibling ABOVE this container (e.g. a
   * pending-changes banner) so the page total stays within 100vh and no scrollbars appear.
   */
  reservedHeight?: number
  children?: ReactNode
}

export type MainContentContainerRef = HTMLDivElement

const useStyles = makeStyles<{
  gap?: IMainContentContainerProps['gap']
  appBarHeight?: number
  reservedHeight?: number
}>()((theme, { gap, appBarHeight, reservedHeight }) => ({
  overflowContainer: {
    overflowY: 'auto',
    height: `calc(100% - ${(appBarHeight ?? 0) + (reservedHeight ?? 0)}px)`,
  },
  main: {
    ...theme.mixins.contentPadding,
    margin: '0',
  },
  maxWidthSm: {
    [theme.breakpoints.up('sm')]: {
      maxWidth: `${theme.breakpoints.values.sm}px`,
    },
  },
  maxWidthMd: {
    [theme.breakpoints.up('md')]: {
      maxWidth: `${theme.breakpoints.values.md}px`,
    },
  },
  maxWidthLg: {
    [theme.breakpoints.up('lg')]: {
      maxWidth: `${theme.breakpoints.values.lg}px`,
    },
  },
  centered: {
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  gap: {
    gap,
    display: 'flex',
    flexDirection: 'column',
  },
}))

const MainContentContainer = forwardRef<MainContentContainerRef, IMainContentContainerProps>(
  ({ children, maxWidth = 'lg', centered = true, gap, domain, reservedHeight }, ref) => {
    const { appBar } = useLayout()
    const router = useRouter()
    const { classes, cx } = useStyles({
      gap,
      appBarHeight: appBar.height,
      reservedHeight,
    })
    const [shouldDisplayBaseSwitchBanner, setShouldDisplayBaseSwitchBanner] = useState(
      !!router.query['base-changed']
    )
    const [shouldDisplayMultiVenueBanner, setShouldDisplayMultiVenueBanner] = useState(
      !!router.query['multi-venue']
    )

    return (
      <div ref={ref} className={classes.overflowContainer}>
        <Container
          disableGutters
          className={cx({
            [classes.main]: true,
            [classes.maxWidthSm]: maxWidth === 'sm',
            [classes.maxWidthMd]: maxWidth === 'md',
            [classes.maxWidthLg]: maxWidth === 'lg',
            [classes.centered]: centered,
            [classes.gap]: gap !== undefined,
          })}
        >
          {shouldDisplayBaseSwitchBanner && (
            <BaseSwitchBanner
              onDismiss={() => setShouldDisplayBaseSwitchBanner(false)}
              domain={domain}
            />
          )}
          {shouldDisplayMultiVenueBanner && (
            <MultiVenueBanner
              onDismiss={() => setShouldDisplayMultiVenueBanner(false)}
              domain={domain}
            />
          )}
          {children}
        </Container>
      </div>
    )
  }
)

MainContentContainer.displayName = 'MainContentContainer'

export default MainContentContainer
